import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Divider,
  Link,
} from '@heroui/react';
import { ThemeSwitcher } from '@/components/shared/ThemeSwitcher';
import TermsModal from '@/components/shared/TermsModal';
import PrivacyPolicyModal from '@/components/shared/PrivacyPolicyModal';
import LoginModal from '@/components/shared/LoginModal'; // Import the LoginModal component
import macroLogo from '@/assets/logo-macroindustrial-tr.png';

const ConfirmationAccount = () => {
  const [searchParams] = useSearchParams();
  const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false); // State for login modal

  useEffect(() => {
    // Extract confirmation token from URL parameters
    const confirmationParam = searchParams.get('confirmation');
    setConfirmationToken(confirmationParam);
  }, [searchParams]);

  const handleLogin = () => {
    setIsLoginModalOpen(true); // Open the login modal instead of navigating
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl bg-white dark:bg-gray-800">
          <CardHeader className="flex flex-col items-center py-8">
            <div className="w-full flex justify-end pr-4">
              <ThemeSwitcher />
            </div>
            <div className="flex justify-center mb-4">
              <img 
                src={macroLogo} 
                alt="MacroIndustrial" 
                className="h-16 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">¡Cuenta Confirmada!</h1>
            <p className="text-gray-600 dark:text-gray-300 text-center mt-2">
              Tu cuenta ha sido verificada exitosamente
            </p>
          </CardHeader>
          
          <Divider className="dark:bg-gray-700" />
          
          <CardBody className="py-6 px-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">Información de Confirmación</h2>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-green-50 dark:bg-gray-700 p-2 rounded-full mr-3">
                    <svg className="w-5 h-5 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Token de confirmación</p>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-300 break-all">
                      {confirmationToken 
                        ? `${confirmationToken.substring(0, 8)}...${confirmationToken.substring(confirmationToken.length - 8)}` 
                        : 'No proporcionado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p className="text-green-700 dark:text-green-300 text-sm">
                  Tu cuenta ha sido activada correctamente. Ahora puedes iniciar sesión en el sistema.
                </p>
              </div>
            </div>
          </CardBody>
          
          <CardFooter className="flex flex-col py-6 px-8">
            <Button 
              variant="solid"
              onPress={handleLogin}
              className="w-full"

            >
              Iniciar Sesión
            </Button>
            
            <p className="text-xs text-gray-500 mt-4 text-center dark:text-gray-400">
              By signing up or signing in, you agree to our{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTermsModalOpen(true);
                }}
              >
                Terms
              </Link>{' '}
              and{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPrivacyModalOpen(true);
                }}
              >
                Privacy Policy
              </Link>.
            </p>
          </CardFooter>
        </Card>
        
        {/* Login Modal */}
        <LoginModal 
          isOpen={isLoginModalOpen} 
          onClose={() => setIsLoginModalOpen(false)} 
        />
        
        {/* Terms Modal */}
        <TermsModal 
          isOpen={isTermsModalOpen} 
          onClose={() => setIsTermsModalOpen(false)} 
        />
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal 
          isOpen={isPrivacyModalOpen} 
          onClose={() => setIsPrivacyModalOpen(false)} 
        />
      </div>
    </div>
  );
};

export default ConfirmationAccount;